<template>
  <div class="software-links">
    <template v-if="readonly">
      <p v-if="!links.length" class="text--secondary">-</p>
      <v-row v-for="(link, index) in links" :key="link.id || index" dense>
        <v-col cols="12" sm="4" class="py-1 font-weight-medium">
          {{ displayLabel(link) }}
        </v-col>
        <v-col cols="12" sm="8" class="py-1 software-link-url">
          <a :href="link.url" target="_blank" rel="noopener noreferrer">{{ link.url }}</a>
        </v-col>
      </v-row>
    </template>

    <template v-else>
      <v-row
        v-for="(link, index) in links"
        :key="link.localId || link.id || index"
        dense
        align="start"
        class="software-link-row"
      >
        <v-col cols="12" sm="4" md="3" class="py-0">
          <v-select
            :value="link.link_type"
            :items="linkTypes"
            item-text="text"
            item-value="value"
            :label="$t('form.software_link_type')"
            :rules="rules.fieldRequired"
            outlined
            hide-details="auto"
            @input="updateLink(index, 'link_type', $event)"
          />
        </v-col>
        <v-col cols="12" sm="7" md="4" class="py-0">
          <v-text-field
            :value="link.label"
            :label="$t('form.software_link_label')"
            :rules="rules.char_max_120"
            outlined
            hide-details="auto"
            @input="updateLink(index, 'label', $event)"
          />
        </v-col>
        <v-col cols="10" sm="11" md="4" class="py-0">
          <v-text-field
            :value="link.url"
            :label="$t('form.software_link_url')"
            :rules="urlRules"
            outlined
            hide-details="auto"
            @input="updateLink(index, 'url', $event)"
          >
            <template v-slot:append-outer>
              <v-tooltip v-if="isOpenableUrl(link.url)" top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="primary"
                    :href="link.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    :aria-label="$t('form.open_link')"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon small>mdi-open-in-new</v-icon>
                  </v-btn>
                </template>
                {{ $t("form.open_link") }}
              </v-tooltip>
            </template>
          </v-text-field>
        </v-col>
        <v-col cols="2" sm="1" class="py-0 d-flex justify-end">
          <v-btn
            icon
            color="error"
            :aria-label="$t('button.delete')"
            :title="$t('button.delete')"
            class="mt-2"
            @click="removeLink(index)"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <v-btn text small color="primary" class="mt-2" @click="addLink">
        <v-icon left>mdi-plus</v-icon>
        {{ $t("form.software_add_link") }}
      </v-btn>
    </template>
  </div>
</template>

<script>
import validationRules from "@/mixins/validationRules";

export default {
  name: "SoftwareLinksField",
  mixins: [validationRules],
  props: {
    links: {
      type: Array,
      default: () => []
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    linkTypes() {
      return [
        "website",
        "remote_access",
        "repository",
        "source_download",
        "binary_download",
        "package",
        "container",
        "app_store",
        "documentation",
        "demo",
        "support",
        "other"
      ].map(value => ({
        value,
        text: this.$t(`form.software_link_types.${value}`)
      }));
    },
    urlRules() {
      return [...this.rules.fieldRequired, ...this.rules.uriRules];
    }
  },
  methods: {
    displayLabel(link) {
      return link.label || this.$t(`form.software_link_types.${link.link_type}`);
    },
    isOpenableUrl(url) {
      if (!url || typeof url !== "string") {
        return false;
      }
      try {
        const parsedUrl = new URL(url);
        return ["http:", "https:"].includes(parsedUrl.protocol);
      } catch (error) {
        return false;
      }
    },
    addLink() {
      this.$emit("input", [
        ...this.links,
        {
          localId: `${Date.now()}-${this.links.length}`,
          link_type: "website",
          label: "",
          url: ""
        }
      ]);
    },
    updateLink(index, field, value) {
      const links = this.links.map((link, position) =>
        position === index ? { ...link, [field]: value } : link
      );
      this.$emit("input", links);
    },
    removeLink(index) {
      this.$emit("input", this.links.filter((_link, position) => position !== index));
    }
  }
};
</script>

<style scoped>
.software-link-row + .software-link-row {
  margin-top: 8px;
}

.software-link-url {
  overflow-wrap: anywhere;
}
</style>
